import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { TradableAreaRegistrationIssue_MissingMaster } from '@ebalance/proto/bffuser/v1/tradable_area_service_pb'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Text } from '@enechain/ecloud-designsystem'

import type { MultiSelectOption } from '~/components/common/MultiSelect'
import { MultiSelect } from '~/components/common/MultiSelect'
import { convertAreaLabel } from '~/models/area'
import type { AreaFilterAvailability } from '~/pages/SpotBidding/models/pageIssuerScope'

/**
 * エリアフィルタ (MultiSelect) の幅。 時間前入札の `IntradayAreaMultiSelect` (`#intraday-area-ms`) と
 * 同じ 220px を踏襲する。
 */
const AREA_FILTER_WIDTH = '220px'

type Props = {
  /**
   * 選択中エリアの実集合 (= 親が持つ state。 sentinel なし)。
   *
   * AC-BSP-019「エリア（複数選択、 入札一覧のエリア複数選択は維持する）」を、
   * 選んだエリアの実集合として表す。 0 件選択 (= 全解除) は widget 側では
   * 空選択のまま表示し (placeholder「全エリア」)、 「空選択 = 取引可能な全エリア」
   * への正規化は呼び出し元 (index.tsx) が行う (design `spot.html`
   * `window.__spotSelectedAreas` と同じ規約。 選択肢は業務エリア設定で絞った
   * `areaCodes`)。 チャート側の単一/複数判定
   * (AC-BSP-016a) は本 component が渡す**正規化前**の実集合を見る
   * (design `getSpotChartArea` が `__spotSelectedAreasRaw` を見るのと同じ)。
   */
  selectedAreas: Set<AreaCode>
  onChange: (next: Set<AreaCode>) => void
  /**
   * 選択肢に出すエリア (JEPX 対象エリア、 北→南順)。 選べるかは [[availabilityOf]] が決める。
   */
  areaCodes: readonly AreaCode[]
  /**
   * 各エリアを選べるか (選べない時の理由の種別つき)。 ツールバーで選んだ発注主体区分で
   * 決まる (AC-BSP-019 / AC-BSP-005b。 `models/pageIssuerScope` の `areaFilterAvailability`)。
   * 選べない理由の Tooltip の文言はこの種別で決まる: 区分で決まる種別は区分ごとの文言
   * (design `spot.html` `_spotAreaOptions`)、 `orgUnregistered` (未選択・申請セットに絞り込み中)
   * は組織全体の文言。
   */
  availabilityOf: (area: AreaCode) => AreaFilterAvailability
  /** AC-IJM-012: backend が判定した、選択中区分×エリアの不足マスタ。 */
  missingMasterOf?: (
    area: AreaCode,
  ) => TradableAreaRegistrationIssue_MissingMaster | undefined
  /**
   * 見えるラベル「エリア」を描かない (accessible name の aria-label は残す)。
   * 開閉式ツールバーの [[FilterGrid]] がラベルを出す時に、二重表示を避けるために使う。
   */
  hideLabel?: boolean
}

/**
 * 入札一覧のエリアフィルタ (design `bidding/spot.html` `#spot-bid-area-ms`)。
 *
 * **複数選択の MultiSelect** (= design 共通 EclMultiSelect、 時間前入札の
 * `IntradayAreaMultiSelect` と同型)。 AC-BSP-019「エリア（複数選択、 入札一覧のエリア複数選択は
 * 維持する）」 / issue #4102 PdM 判断 (2026-08-03 倉淵)「エリアは複数選択できる。
 * ただしグラフはエリアごとでないと見られないため、 multi-select 中はチャート
 * 領域にガイドを出す」に基づく。
 *
 * 2026-07-28〜2026-09 は単一選択 radio だったため spec 違反の状態だった
 * (旧実装・経緯は issue #4102 参照。 spec/design は 2026-08-03 の PdM 判断で
 * 複数選択へ確定済みで、 本 component が最後まで追随していなかった)。
 *
 * 選べないエリア (`availabilityOf`。 AC-BSP-005b の広域マスタ登録軸と業務エリア設定) は
 * **選択肢に残したまま選択不可 (グレーアウト) にし、 ホバーで理由を Tooltip 表示する**
 * (`MultiSelect` の option 単位 `disabled` / `disabledReason`)。 区分で未登録のエリアには
 * backend が判定した不足マスタの名前 (AC-IJM-012) を、 業務エリア外のエリアには
 * 「業務エリア対象外」 を、 補助テキストとして名前の下 (2 行目) に出す (`description`)。
 * 不足マスタが届かない時・未知の値の時は推測せず出さない。
 * 会員情報による発注可否では閲覧を制限しない (AC-IJM-013)。 AC-BSP-005b の規定で、
 * 札を作るモーダルが広域マスタ未登録のエリアを選択肢に出さないのとは扱いが違う。
 *
 * 共通ツールバー (AC-BSP-131、 [[SpotPageHeader]]) の 1 項目として配置する。
 * 外枠はツールバーが持ち、この部品はエリアのラベルと選択欄だけを描画する。
 */
export const AreaFilterBar = ({
  selectedAreas,
  onChange,
  areaCodes,
  availabilityOf,
  missingMasterOf,
  hideLabel = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  const disabledReasonOf = (availability: AreaFilterAvailability): string => {
    switch (availability) {
      case 'orgUnregistered':
        return t('page:biddingSpot.area.unselectableHint')
      case 'outsideBusinessAreas':
        return t('page:biddingCommon.issuerSelector.areaOutsideBusinessAreas')
      default:
        return t('page:biddingSpot.area.unselectableHintIssuerUnregistered')
    }
  }

  const descriptionOf = (
    availability: AreaFilterAvailability,
    area: AreaCode,
  ): string | undefined => {
    switch (availability) {
      case 'unregistered': {
        // 登録集合だけでは不足マスタを判別できない。旧server・未知enumでも推測しない。
        switch (missingMasterOf?.(area)) {
          case TradableAreaRegistrationIssue_MissingMaster.SUBMITTER:
            return t('page:biddingSpot.area.submitterMasterMissing')
          case TradableAreaRegistrationIssue_MissingMaster.DEMAND_PROCUREMENT:
            return t('page:biddingSpot.area.demandProcurementMasterMissing')
          case TradableAreaRegistrationIssue_MissingMaster.BG:
            return t('page:biddingSpot.area.bgMasterMissing')
          default:
            return undefined
        }
      }
      case 'outsideBusinessAreas':
        return t('page:biddingCommon.issuerSelector.areaOutsideBusinessAreas')
      default:
        return undefined
    }
  }

  const areaOptions: MultiSelectOption[] = areaCodes.map((code) => {
    const availability = availabilityOf(code)
    const isDisabled = availability !== 'selectable'
    // 2 行目 (description) がある選択肢は、 そこで理由を読むのでツールチップを重ねない (MultiSelect が
    // 判定する。 2026-10-30 デザイナー判断)。 2 行目が無い選択肢 (組織全体で登録が無い・不足マスタが
    // 分からない) は、 今までどおりツールチップで理由を出す
    return {
      label: convertAreaLabel(code),
      value: String(code),
      disabled: isDisabled,
      disabledReason: isDisabled ? disabledReasonOf(availability) : undefined,
      description: descriptionOf(availability, code),
    }
  })
  // 並びは 呼び出し元が渡す areaCodes (北→南) を SoT にする (`buildSelectedAreasLabel` と同じ規則)。
  const selectedValues = areaCodes
    .filter((code) => selectedAreas.has(code))
    .map(String)

  const handleChange = (next: string[]): void => {
    onChange(new Set(next.map((v) => Number(v) as AreaCode)))
  }

  return (
    <Flex
      alignItems="center"
      flexWrap="wrap"
      flexShrink={0}
      gap={2}
      maxWidth="100%"
    >
      {/* ラベルは自然幅、 ラベルと部品の間は `.filter-group` の 8px (UI 辞書
          `components/toolbar.md`)。 */}
      {!hideLabel && (
        <Text
          textStyle="filterLabel"
          color="Text.highEmphasis"
          whiteSpace="nowrap"
        >
          {t('page:biddingSpot.form.area')}
        </Text>
      )}
      <Box flexShrink={0} width={AREA_FILTER_WIDTH}>
        <MultiSelect
          aria-label={t('page:biddingSpot.form.area')}
          // 選べるエリアを先頭に、 選べないエリアは区切り線と見出しの下にまとめる (2026-10-30 デザイナー判断
          // 「選択できるものとできないものの視覚差分が少ない」。 AC-BSP-005b の並び・見た目は Des 判断範囲)
          disabledGroupLabel={t('page:biddingSpot.area.unavailableGroup')}
          onChange={handleChange}
          options={areaOptions}
          placeholder={t('page:biddingSpot.area.allAreas')}
          selected={selectedValues}
        />
      </Box>
    </Flex>
  )
}
